import React, { useState } from 'react';
import { IoClose } from "react-icons/io5";
import { FaTruck, FaBox, FaMapMarkerAlt, FaSearch, FaCheckCircle } from 'react-icons/fa';
import useTranslate from '../hooks/useTranslate';
import { trackContact } from '../helpers/analyticsHelper';

const TrackOrderDrawer = ({ onClose }) => {
    const [orderId, setOrderId] = useState('');
    const [trackingResult, setTrackingResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const t = useTranslate({
        trackOrder: "Track Order",
        realTimeShipping: "Real-Time Shipping",
        enterOrderId: "Enter Order ID",
        trackPackage: "Track Your Package",
        trackDesc: "See exact updates on your shipping and fulfillment timeline.",
        status: "Status",
        expected: "Expected",
        fulfillmentProcess: "Fulfillment Process",
        contactSupport: "Contact Delivery Support",
        closeTracker: "Close Tracker",
        inTransit: "In Transit",
        orderPlaced: "Order Placed",
        processing: "Processing",
        shipped: "Shipped",
        outForDelivery: "Out for Delivery",
        delivered: "Delivered",
        pending: "Pending"
    });

    React.useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const handleTrack = (e) => {
        e.preventDefault();
        if (!orderId) return;

        setIsLoading(true);

        // Dummy timeout to simulate API loading for UI purposes
        setTimeout(() => {
            setTrackingResult({
                id: orderId,
                status: t.inTransit,
                estimatedDelivery: 'Oct 24, 2026',
                address: '123 Farmer Road, Green Valley District, Punjab, India',
                items: [
                    { name: 'bayer roundup herbicide (glyphosate 41% sl)', qty: 1, price: 158.00 },
                    { name: 'saaf fungicide', qty: 3, price: 267.00 }
                ],
                events: [
                    { status: t.orderPlaced, date: 'Oct 18, 10:00 AM', location: 'Website', done: true },
                    { status: t.processing, date: 'Oct 19, 02:30 PM', location: 'Delhi Warehouse', done: true },
                    { status: t.shipped, date: 'Oct 20, 08:45 AM', location: 'Delhi Facility', done: true },
                    { status: t.inTransit, date: 'Oct 21, 11:20 AM', location: 'Ludhiana Hub', done: true, current: true },
                    { status: t.outForDelivery, date: t.pending, location: '-', done: false },
                    { status: t.delivered, date: t.pending, location: '-', done: false },
                ]
            });
            setIsLoading(false);
        }, 1500);
    };

    return (
        <div className='fixed top-0 bottom-0 left-0 right-0 z-[100] flex justify-end bg-black/40 backdrop-blur-[2px] transition-all animate-fadeIn'>
            <div className='flex-1' onClick={onClose} aria-hidden="true"></div>

            <div className='bg-white h-full w-full max-w-[450px] shadow-2xl flex flex-col animate-slideInRight overflow-hidden relative'>

                {/* Header Profile Section */}
                <div className='bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] px-6 pt-10 pb-6 shrink-0 relative overflow-hidden'>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -translate-y-8 translate-x-8 mix-blend-screen"></div>
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#00b259]/20 rounded-full blur-2xl pointer-events-none"></div>

                    <button
                        className='absolute top-4 right-4 text-white/70 hover:text-white transition-all bg-black/10 hover:bg-black/20 p-2 rounded-full backdrop-blur-sm z-20'
                        onClick={onClose}
                    >
                        <IoClose size={20} />
                    </button>

                    <div className='flex items-center gap-5 relative z-10'>
                        <div className='w-16 h-16 rounded-full bg-white/10 border-2 border-[#00b259] flex items-center justify-center text-white shadow-lg backdrop-blur-md overflow-hidden'>
                            <FaMapMarkerAlt size={28} />
                        </div>
                        <div className="flex flex-col text-white">
                            <h2 className='text-xl justify-center font-black text-white tracking-wide uppercase'>
                                {t.trackOrder}
                            </h2>
                            <p className='text-xs text-[#00b259] font-bold uppercase tracking-widest mt-0.5'>
                                {t.realTimeShipping}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content Body */}
                <div className='flex-1 overflow-y-auto bg-[#f8fcfb] custom-scrollbar'>

                    {/* Search Field Area */}
                    <div className="bg-white p-6 border-b border-gray-100 shadow-sm relative z-10">
                        <form onSubmit={handleTrack} className="flex flex-col gap-3">
                            <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">{t.enterOrderId}</label>
                            <div className="flex h-[48px] rounded-xl overflow-hidden border border-gray-200 focus-within:border-[#00b259] transition-colors shadow-sm">
                                <input
                                    type="text"
                                    placeholder="e.g. FSL-7932"
                                    value={orderId}
                                    onChange={(e) => setOrderId(e.target.value)}
                                    className="w-full h-full px-4 outline-none text-[#143d2c] font-bold text-sm bg-gray-50 focus:bg-white transition-colors"
                                />
                                <button
                                    type="submit"
                                    disabled={!orderId || isLoading}
                                    className="h-full px-6 bg-[#00b259] text-white flex justify-center items-center hover:bg-[#009b4d] disabled:bg-gray-300 transition-colors"
                                >
                                    {isLoading ? (
                                        <div className="w-5 h-5 border-[3px] border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <FaSearch />
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    {!trackingResult && !isLoading && (
                        <div className="flex flex-col items-center justify-center text-center p-12 opacity-60 mt-10">
                            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-5">
                                <FaBox className="text-[#00b259]/40" size={40} />
                            </div>
                            <h2 className="text-lg font-black text-[#143d2c] tracking-wide mb-2">{t.trackPackage}</h2>
                            <p className="text-gray-500 text-xs font-medium px-4">{t.trackDesc}</p>
                        </div>
                    )}

                    {isLoading && (
                        <div className="p-6 flex flex-col gap-6">
                            {/* Summary Card Skeleton */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">
                                <div className="flex justify-between items-end border-b border-gray-50 pb-4">
                                    <div className="space-y-2 w-1/3">
                                        <div className="h-2 shimmer-effect rounded w-1/2"></div>
                                        <div className="h-4 shimmer-effect rounded w-full"></div>
                                    </div>
                                    <div className="space-y-2 w-1/3 flex flex-col items-end">
                                        <div className="h-2 shimmer-effect rounded w-1/2"></div>
                                        <div className="h-4 shimmer-effect rounded w-full"></div>
                                    </div>
                                </div>
                                <div className="h-12 shimmer-effect rounded-xl w-full"></div>
                            </div>

                            {/* Timeline Skeleton */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-6">
                                <div className="h-3 shimmer-effect rounded w-1/3 mb-4"></div>
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="w-4 h-4 shimmer-effect rounded-full shrink-0"></div>
                                        <div className="space-y-2 flex-1">
                                            <div className="h-3 shimmer-effect rounded w-3/4"></div>
                                            <div className="h-2 shimmer-effect rounded w-1/2"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {trackingResult && (
                        <div className="p-6 flex flex-col gap-6 fade-in animate-fadeIn">

                            {/* Summary Card */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 relative overflow-hidden">
                                <div className="absolute right-0 top-0 w-24 h-24 bg-[#00b259]/5 rounded-bl-[100px] pointer-events-none"></div>

                                <div className="flex justify-between items-end border-b border-gray-50 pb-4 mb-4">
                                    <div>
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">{t.status}</p>
                                        <h3 className="text-[#00b259] font-black uppercase text-base">{trackingResult.status}</h3>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">{t.expected}</p>
                                        <h3 className="text-[#143d2c] font-black uppercase text-sm">{trackingResult.estimatedDelivery}</h3>
                                    </div>
                                </div>

                                <div className="flex bg-gray-50 p-3 rounded-xl items-start gap-3 border border-gray-100">
                                    <FaMapMarkerAlt className="text-orange-400 mt-1 flex-shrink-0" />
                                    <p className="text-[#143d2c] text-xs font-semibold leading-relaxed">
                                        {trackingResult.address}
                                    </p>
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 pb-2">
                                <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-6">{t.fulfillmentProcess}</h3>

                                <div className="relative ml-2 space-y-0">
                                    <div className="absolute top-2 bottom-8 left-[-11px] border-l-2 border-gray-100"></div>

                                    {trackingResult.events.map((event, idx) => (
                                        <div key={idx} className="relative pl-6 pb-8 group">
                                            {/* Step point */}
                                            <div className={`absolute -left-[18px] top-0 w-4 h-4 rounded-full border-[3px] flex items-center justify-center bg-white z-10 transition-all 
 ${event.done ? 'border-[#00b259]' : 'border-gray-200'}
 ${event.current && 'ring-4 ring-[#00b259]/20 border-[#00b259]'}
 `}></div>

                                            <div className="flex flex-col gap-1.5 -mt-1.5 opacity-90">
                                                <div className="flex justify-between items-start gap-2">
                                                    <h4 className={`text-sm font-black tracking-tight ${event.done ? 'text-[#143d2c]' : 'text-gray-400'}`}>
                                                        {event.status}
                                                    </h4>
                                                    <span className={`text-[10px] font-bold whitespace-nowrap ${event.done ? 'text-gray-500' : 'text-gray-300'}`}>
                                                        {event.date}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-gray-500 font-medium tracking-wide flex items-center gap-1.5">
                                                    <FaMapMarkerAlt className={event.done ? "text-orange-400 w-3" : "text-gray-300 w-3"} />
                                                    {event.location}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Contact Box */}
                            <a
                                href={`https://wa.me/916266761336?text=${encodeURIComponent(`Hello FasalShop Support, I need help with my order tracking (Order ID: ${trackingResult?.id || orderId}).`)}`}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={() => trackContact({ method: 'WhatsApp', location: 'Track Order Drawer Support' })}
                                className="bg-[#f0f9ff] text-[#0284c7] border border-[#bae6fd] py-3.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors shadow-sm flex justify-center items-center gap-2 hover:bg-[#e0f2fe] w-full cursor-pointer"
                            >
                                <FaTruck size={14} /> {t.contactSupport}
                            </a>

                        </div>
                    )}
                </div>

                {/* FOOTER CLOSE BUTTON */}
                <div className="p-5 bg-white border-t border-gray-100 mt-auto">
                    <button
                        onClick={onClose}
                        className="w-full bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] hover:brightness-110 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 transition-all uppercase tracking-[3px] text-[11px] shadow-xl shadow-green-900/20 active:scale-[0.98]"
                    >
                        {t.closeTracker}
                    </button>
                </div>
            </div>

            {/* Injected animations logic inside tailwind if not present globally, fallback custom css */}
            <style jsx="true">{`
 .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
 .animate-slideInRight { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
 @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
 @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
 `}</style>
        </div>
    );
};

export default TrackOrderDrawer;
